import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { FaArrowsAltH, FaArrowsAltV } from 'react-icons/fa';
import ButtonComponent from 'components/ButtonComponent';
import { uploadFile } from 'lib/communication/files';
import { INSERT_IMAGE_COMMAND } from '../InsertImagePlugin';
import './ImageInsertModal.scss';
import { trackLink } from '../linkRegistry';

const IMAGE_ACCEPT = 'image/gif,image/jpeg,image/jpg,image/png,image/svg+xml';
const ACCEPTED_IMAGE_TYPES = IMAGE_ACCEPT.split(',');
const MAX_IMAGE_SIZE = 2097152; // 2MB, matches legacy ImageLayout.tsx

type ImageInsertModalProps = {
  show: boolean,
  onClose: () => void
}
const ImageInsertModal = ({ show, onClose  }: ImageInsertModalProps) => {
  const [editor] = useLexicalComposerContext();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [altTextInput, setAltTextInput] = useState<string>('');
  const [imageWidthInput, setImageWidthInput] = useState<string>('auto');
  const [imageHeightInput, setImageHeightInput] = useState<string>('auto');

  const reset = () => {
    setImageFile(null);
    setAltTextInput('');
    setImageWidthInput('auto');
    setImageHeightInput('auto');
    setImageError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleImageFileChange = (file: File | null) => {
    if (file && !ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setImageFile(null);
      setImageError('Niedozwolony format pliku. Dozwolone formaty: gif, jpeg, jpg, png, svg.');
      return;
    }
    if (file && file.size > MAX_IMAGE_SIZE) {
      setImageFile(null);
      setImageError('Plik jest za duży. Maksymalny rozmiar obrazka to 2MB.');
      return;
    }
    setImageFile(file);
    setImageError(null);
  };

  const applyInsertImage = async () => {
    if (!imageFile) return;
    try {
      const link = await uploadFile(imageFile);
      editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
        src: link,
        altText: altTextInput,
        width: imageWidthInput,
        height: imageHeightInput
      });
      trackLink(editor,link);
      handleClose();
    } catch (e: any) {
      setImageError(e?.message ?? String(e));
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton><Modal.Title>Wgraj obrazek</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className='ImageModal'>
          <input
            type='file'
            accept={IMAGE_ACCEPT}
            onChange={(e) => handleImageFileChange(e.target.files?.[0] ?? null)}
          />
          <input
            placeholder='Tekst alternatywny'
            value={altTextInput}
            className='ImageModal__input'
            onChange={(e) => setAltTextInput(e.target.value)}
          />
          <div className='ImageModal__size_container'>
            <FaArrowsAltV />
            <input
              placeholder="Wysokość"
              value={imageHeightInput}
              className='ImageModal__size_container__input'
              onChange={(e) => setImageHeightInput(e.target.value)}
            />
            x
            <input
              placeholder="Szerokość"
              value={imageWidthInput}
              className='ImageModal__size_container__input'
              onChange={(e) => setImageWidthInput(e.target.value)}
            />
            <FaArrowsAltH />
          </div>
          {imageError && <div className='ImageModal__error'> {imageError} </div>}
        </div>
        <ButtonComponent text='Wgraj' onClick={applyInsertImage} />
        <ButtonComponent text='Anuluj' onClick={handleClose} />
      </Modal.Body>
    </Modal>
  );
}

export default ImageInsertModal
